import React from "react";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useFormik } from "formik";
import { RRule } from "rrule";

import SVG from "@/assets/svg";
import CostumeButton from "@/component/button";
import AdditionalRecordedMetadata from "@/component/care-planning/AdditionalRecordedMetadata";
import {
  DateInput,
  LabeledInput,
  RadioInput,
  TimeInput,
} from "@/component/input";
import { dismissAllToasts, errorToast, successToast } from "@/component/toasts";
import { ScheduleVisit, Visit } from "@/interfaces/IServiceUser";
import { TaskManagementResponse } from "@/interfaces/ITaskManagement";
import {
  createVisitAsync,
  deleteManyVisitAsync,
  deleteVisitAsync,
  getVisitsByOriginAsync,
  updateMultipleVisitsAsync,
  updateVisitAsync,
} from "@/redux/actions/roster";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setSelectedVisit, setVisitsByOrigin } from "@/redux/reducers/roster";
import { deleteTask } from "@/services/task-management";
import { useSocket } from "@/context";
import { INVISIBLE_CHAR } from "@/utils/constants";
import {
  getRRule,
  getRRuleAsObject,
  getSession,
  RRuleFrequencies,
  rruleWeekDays,
} from "@/utils/helpers";
import logger from "@/runtime-utils/observability/logger";
import { getScheduleVisitTrace } from "@/utils/scheduleVisitTrace";
import { ScheduleValidationSchema } from "@/utils/validators";
import DialogueBox from "..";
import ConfirmDelete from "../confirm-delete";
import OverrideIncompatibilityModal from "../overrideIncompatibility";
import SaveChange from "../save-change";
import VisitLayout from "./visit";

dayjs.extend(utc);

interface IVisit {
  onClose: () => void;
}

function VisitDetail({ onClose }: IVisit) {
  const dispatch = useAppDispatch();
  const socket = useSocket();
  const {
    selectedVisit,
    visits: { data: visits },
    visitsByOrigin: { data: visitsByOrigin } = { data: [] },
  } = useAppSelector((state) => state.roster);

  const visit = selectedVisit?.extendedProps as Visit;

  const isOriginal = !visit?.origin;

  const [showEndDate, setShowEndDate] = React.useState(
    (isOriginal ? visit?.endDate : visit?.origin?.endDate) !== "never"
  );
  const [selectedVisitEndDate, setSelectedVisitEndDate] = React.useState<
    Date | "never"
  >(visit?.origin?.endDate || visit?.endDate || "never");
  const [saveChanges, setSaveChanges] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [tasks, setTasks] = React.useState<TaskManagementResponse[]>([]);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null
  );
  const [taskDeleteLoading, setTaskDeleteLoading] = React.useState(false);
  const [visitDeleteLoading, setVisitDeleteLoading] = React.useState(false);
  const [conflictData, setConflictData] = React.useState<{
    message: string;
    changeAll: boolean;
  } | null>(null);
  const [selectedVisitIds, setSelectedVisitIds] = React.useState<
    string[] | null
  >(null);

  const handleClose = () => {
    setSaveChanges(false);
  };

  const {
    values,
    touched,
    errors,
    setFieldError,
    setFieldValue,
    getFieldProps,
  } = useFormik<Partial<Visit>>({
    initialValues: visit,
    enableReinitialize: true,
    validationSchema: ScheduleValidationSchema,
    onSubmit: async () => {},
  });

  const getChildVisits = async (id: string) => {
    try {
      const resultAction = await dispatch(
        getVisitsByOriginAsync({
          id,
          startDate:
            dayjs(visit?.startDate).toDate().toISOString() ||
            dayjs().toDate().toISOString(),
          // endDate,
        })
      ).unwrap();
      return resultAction?.data || [];
    } catch (error) {
      logger.error("Failed to fetch visit by origin:", error);
    }
  };

  const handleRootClose = () => {
    dispatch(setVisitsByOrigin([]));
    onClose();
  };

  const handleManyVisitDelete = async () => {
    try {
      setVisitDeleteLoading(true);
      const normalizeToDate = (d: Date | string): string =>
        new Date(d).toISOString().split("T")[0]; // YYYY-MM-DD
      const rootVisit: Visit | ScheduleVisit = visit?.origin ?? visit;
      const idSet = new Set(selectedVisitIds ?? []);

      // Collect selected visit dates (normalized)
      const deleteDateSet = new Set(
        (visitsByOrigin ?? []).reduce<string[]>((acc, v: Visit) => {
          if (idSet.has(String(v._id)) && v.startDate) {
            acc.push(normalizeToDate(v.startDate));
          }
          return acc;
        }, [])
      );

      // Filter existing exdates (match by date only)
      const updatedExDate = Array.from(
        new Set(
          (rootVisit.exdate ?? []).filter(
            (d) => !deleteDateSet.has(normalizeToDate(d))
          )
        )
      );

      if (rootVisit?._id) {
        await dispatch(
          updateVisitAsync({
            id: rootVisit._id,
            data: { exdate: updatedExDate },
          })
        ).unwrap();
      }
      await dispatch(deleteManyVisitAsync({ ids: selectedVisitIds })).unwrap();

      setSelectedVisitIds(null);
      // successToast("After end date, all visits deleted successfully");
    } catch (error: any) {
      logger.error("Failed to delete visits:", error);
    } finally {
      setVisitDeleteLoading(false);
    }
  };

  const handleScheduleValidate = async () => {
    if (!values?.startTime || !values?.endTime) return;

    if (!values.carerReq) {
      setFieldError("carerReq", "Carer is required");
      return errorToast("Carer is required");
    }

    if (!Number.isInteger(values.carerReq)) {
      setFieldError("carerReq", "Carer must be a whole number");
      return errorToast("Carer must be a whole number");
    }

    if (
      showEndDate &&
      (!selectedVisitEndDate || selectedVisitEndDate === "never")
    ) {
      setFieldError("endDate", "Please select end date");
      return errorToast("Please select end date");
    }

    const normalize = (d?: string | Date | null): dayjs.Dayjs | null =>
      d && d !== "never" ? dayjs(d).startOf("day") : null;
    const visitRaw: string | Date | "never" =
      visit?.origin?.endDate || visit?.endDate || "never";
    const visitDate = normalize(visitRaw);
    const selectedDate = normalize(selectedVisitEndDate);

    // only true if strictly past (not today, not future, not same)
    const isPast: boolean =
      !!selectedDate && !!visitDate && selectedDate.isBefore(visitDate);

    if ((visitRaw === "never" && selectedDate) || (visitDate && isPast)) {
      const childVisits: Visit[] = visitsByOrigin?.length
        ? visitsByOrigin
        : ((await getChildVisits(visit?.origin?._id ?? visit?._id)) ?? []);

      const filteredChildVisits: Visit[] = childVisits.filter((v) => {
        const end = normalize(v.endDate);
        return !!end && !!selectedDate && end.isAfter(selectedDate);
      });

      setSelectedVisitIds(filteredChildVisits.map((v) => v._id));
    }

    const startTime = dayjs(values?.startTime);
    const endTime = dayjs(values?.endTime);

    try {
      if (startTime.format("HH:mm") === endTime.format("HH:mm")) {
        setFieldError("endTime", "End time cannot be the same as start time");
        return errorToast("End time cannot be the same as start time");
      }

      setSaveChanges(true);
    } catch (error) {
      logger.error(error);
    }
  };

  const handleSave = async (changeAll: boolean) => {
    try {
      setLoading(true);
      // --- Prepare requests (no duplication) ---
      const requests = [];
      const rootVisitId = visit?.origin?._id ?? visit?._id;
      // const childVisits = visits?.filter((v) => v?.origin?._id === rootVisitId) ?? [];
      let childVisits: Visit[] = [];
      if (rootVisitId && changeAll) {
        childVisits =
          visitsByOrigin?.length > 0
            ? visitsByOrigin
            : await getChildVisits(rootVisitId);
      }

      const VISIT_FIELDS = [
        "carerReq",
        "startTime",
        "endTime",
        "endDate",
      ] as const;
      const payload: Partial<Visit> = Object.fromEntries(
        VISIT_FIELDS.map((k) => [k, values[k]]).filter(
          ([, v]) => v !== undefined
        )
      );
      payload.endDate = selectedVisitEndDate;

      // --- Validate ---
      if (payload?.carerReq) {
        const scope = changeAll ? childVisits : [visit];

        if (
          scope.some((v) => (v?.carerAssigned?.length || 0) > payload.carerReq!)
        ) {
          return errorToast(
            "Carer requirement cannot be less than assigned carers"
          );
        }
      }

      const { endDate, ...childPayload } = payload;

      // children OR single
      if (changeAll) {
        // root update
        if (rootVisitId) {
          requests.push(
            dispatch(
              updateVisitAsync({
                id: rootVisitId,
                data: payload,
              })
            ).unwrap()
          );
        }
        if (childVisits?.length) {
          requests.push(
            dispatch(
              updateMultipleVisitsAsync({
                ids: childVisits?.map((v) => v._id),
                updateData: childPayload,
              })
            ).unwrap()
          );
        }
      } else if (!changeAll && visit?._id) {
        // root update
        if (rootVisitId) {
          requests.push(
            dispatch(
              updateVisitAsync({
                id: rootVisitId,
                data: { endDate },
              })
            ).unwrap()
          );
        }

        if (!visit?.origin) {
          const { _id, ...newVisit } = visit;

          requests.push(
            dispatch(
              updateVisitAsync({
                id: visit?._id,
                data: {
                  exdate: [
                    ...new Set([
                      ...(visit?.exdate ? visit.exdate : []),
                      dayjs(selectedVisit?.start).format("YYYY-MM-DD"),
                    ]),
                  ],
                },
              })
            ).unwrap()
          );

          requests.push(
            dispatch(
              createVisitAsync({
                ...newVisit,
                origin: _id,
                rrule: newVisit?.originalRRule || { frequency: "daily" },
                serviceUser: newVisit?.serviceUser?._id,
                startDate: selectedVisit?.start,
                endDate: selectedVisit?.start,
                exdate: [],
                notes: newVisit?.notes?.map((n) => n._id),
                carerAssigned: newVisit?.carerAssigned?.map((c) => c?._id),
                anotherCarers: newVisit?.anotherCarers?.map(
                  (n) => n?.carer?._id
                ),
                cancelOptions: payload,
                ...childPayload,
              })
            )
          );
        } else {
          requests.push(
            dispatch(
              updateVisitAsync({
                id: visit._id,
                data: childPayload,
              })
            ).unwrap()
          );
        }
      }

      await Promise.all(requests);
      if (selectedVisitIds?.length > 0) {
        await handleManyVisitDelete();
      }
      socket.emit("visit:refreshAll"); // Trigger a full refresh because modifying the end date regenerates recurring visits on the backend, making local state unreliable.
      dispatch(setSelectedVisit(null));
      onClose();
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data || error;
      const isConflict =
        errorData?.type === "INCOMPATIBILITY_CONFLICT" ||
        (typeof errorData?.message === "string" &&
          errorData.message.includes("Incompatible match"));

      if (error?.response?.status === 409 && isConflict) {
        setConflictData({
          message: error.response.data.message,
          changeAll,
        });
        dismissAllToasts();
        return;
      }

      const msg =
        errorData?.message || error?.message || "Failed to update visit";
      errorToast(msg, {
        autoClose: 3500,
      });
      logger.error("Failed to update visit:", error);
    } finally {
      setLoading(false);
      setSaveChanges(false);
      dispatch(setVisitsByOrigin([]));
    }
  };

  const handleDeleteTask = async () => {
    try {
      setTaskDeleteLoading(true);
      const res = await deleteTask(selectedTaskId as string);
      if (res.success) {
        setTasks(tasks.filter((task) => task._id !== selectedTaskId));
        setSelectedTaskId(null);
        successToast("Task deleted successfully");
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setTaskDeleteLoading(false);
    }
  };

  return (
    <>
      <div>
        <VisitLayout>
          <Box className="Add-medication">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "19px",
              }}
            >
              <Typography id="modal-modal-title" variant="h3">
                {visit?.serviceUser?.name}&apos;s {visit?.session} visit
              </Typography>
              <Box onClick={handleRootClose}>
                <SVG.Cross />
              </Box>
            </Box>
            {visit && (
              <AdditionalRecordedMetadata
                record={{
                  createdBy: { name: visit.createdBySummary ?? undefined },
                  updatedBy: { name: visit.lastUpdatedBySummary ?? undefined },
                  createdAt: visit.createdAt,
                  updatedAt: visit.updatedAt,
                }}
              />
            )}
            <Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box sx={{ padding: "5px 0px", borderRadius: "8px" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        padding: "5px 0px",
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#1F4970",
                      }}
                    >
                      Care Team
                    </Typography>
                    <FormGroup>
                      <LabeledInput
                        label="Carers required*"
                        type="number"
                        className="personal"
                        slotProps={{ htmlInput: { step: 1, min: 1 } }}
                        {...getFieldProps("carerReq")}
                        onFocus={(e) => e.target.select()}
                      />
                    </FormGroup>
                    <Box
                      sx={{
                        marginBlock: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          padding: "5px 0px",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1F4970",
                        }}
                      >
                        When
                      </Typography>
                      <div
                        style={{
                          display: "flex",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <span
                            style={{
                              marginTop: "10px",
                              fontSize: "12px",
                            }}
                            className="dayData"
                          >
                            Date
                          </span>
                          <span className="dayData">
                            {dayjs(selectedVisit?.start)
                              .utc()
                              .format("dddd DD MMM")}
                          </span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span
                            style={{
                              marginTop: "10px",
                              fontSize: "12px",
                            }}
                            className="dayData"
                          >
                            Frequency
                          </span>
                          {visit && (
                            <span className="dayData">
                              {(() => {
                                try {
                                  const rule = getRRule(visit);
                                  // RRuleSet doesn't have toText, so we get the first rule
                                  const rrule = rule.rrules()[0];
                                  return rrule ? rrule.toText() : "Custom";
                                } catch (err) {
                                  logger.error(
                                    getScheduleVisitTrace(visit, err),
                                    "Schedule visit recurrence parsing failed"
                                  );
                                  return "Custom";
                                }
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid size={6}>
                        <FormGroup>
                          <TimeInput
                            label="Select time*"
                            value={dayjs(values.startTime)}
                            onChange={(value) => {
                              setFieldValue("startTime", value);
                              if (value?.toDate()) {
                                const session = getSession(value?.toDate());
                                setFieldValue("session", session);
                              }
                            }}
                            errorText={touched.startTime && errors.startTime}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid size={6}>
                        <FormGroup className="gender">
                          <TimeInput
                            label={INVISIBLE_CHAR} // invisible label to maintain the alignment with error state
                            value={dayjs(values.endTime)}
                            onChange={(value) => {
                              setFieldValue("endTime", value);
                            }}
                            errorText={touched.endTime && errors.endTime}
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid size={12}>
                        <FormGroup className="gender">
                          <RadioInput
                            label="End"
                            value={showEndDate ? "on" : "never"}
                            onChange={(e, val) => {
                              if (val === "never") {
                                setShowEndDate(false);
                                setFieldValue("endDate", "never");
                                setSelectedVisitEndDate("never");
                              } else {
                                setShowEndDate(true);
                                setFieldValue("endDate", undefined);
                                setSelectedVisitEndDate(
                                  visit?.origin?.endDate ||
                                    visit?.endDate ||
                                    "never"
                                );
                              }
                            }}
                            options={[
                              {
                                label: "Never",
                                value: "never",
                              },
                              {
                                label: "On",
                                value: "on",
                              },
                            ]}
                          />
                        </FormGroup>
                      </Grid>

                      {showEndDate && (
                        <Grid size={12}>
                          <DateInput
                            onChange={(val) => {
                              setSelectedVisitEndDate(dayjs(val).toDate());
                              setFieldValue("endDate", val);
                              if (val) {
                                sessionStorage.setItem(
                                  "endDate",
                                  val.format("YYYY-MM-DD")
                                );
                              } else {
                                sessionStorage.removeItem("endDate");
                              }
                            }}
                            value={
                              selectedVisitEndDate
                                ? dayjs(selectedVisitEndDate)
                                : values.endDate
                                  ? dayjs(values.endDate)
                                  : sessionStorage.getItem("endDate")
                                    ? dayjs(sessionStorage.getItem("endDate"))
                                    : values.origin?.endDate
                                      ? dayjs(values.origin.endDate)
                                      : undefined
                            }
                            minDate={
                              values.origin?.startDate
                                ? dayjs(values.origin.startDate).isAfter(
                                    dayjs(),
                                    "day"
                                  )
                                  ? dayjs(values.origin.startDate)
                                  : dayjs()
                                : dayjs()
                            }
                            errorText={
                              touched?.endDate &&
                              typeof errors?.endDate === "string"
                                ? errors.endDate
                                : undefined
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CostumeButton
                        className="invite"
                        sx={{
                          marginInline: "0px !important",
                          marginTop: "2rem !important",
                        }}
                        onClick={handleScheduleValidate}
                      >
                        Save Change
                      </CostumeButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <DialogueBox onClose={handleClose} open={saveChanges}>
            <SaveChange
              visitTime={selectedVisit?.start}
              isPastVisit={dayjs(selectedVisit?.start).isBefore(
                dayjs().startOf("day")
              )}
              loading={loading}
              handleSave={handleSave}
              onClose={handleClose}
              warningMsg={
                selectedVisitIds?.length > 0
                  ? `You're setting an earlier end date. Any visits scheduled after this date will be removed. This action cannot be undone.`
                  : ""
              }
            />
          </DialogueBox>
          <DialogueBox
            onClose={() => setSelectedTaskId(null)}
            open={selectedTaskId !== null}
          >
            <ConfirmDelete
              loading={taskDeleteLoading}
              handleDelete={handleDeleteTask}
              onClose={() => setSelectedTaskId(null)}
            />
          </DialogueBox>

          <DialogueBox
            onClose={() => setConflictData(null)}
            open={!!conflictData}
          >
            {conflictData && (
              <OverrideIncompatibilityModal
                message={conflictData.message}
                serviceUserId={visit.serviceUser?._id}
                onClose={() => setConflictData(null)}
                onSubmit={(reason) => {
                  setFieldValue("overrideReason", reason);
                  setConflictData(null);
                  setTimeout(() => handleSave(conflictData.changeAll), 0);
                }}
              />
            )}
          </DialogueBox>
        </VisitLayout>
      </div>
    </>
  );
}

export default VisitDetail;
